import { createAsyncThunk } from "@reduxjs/toolkit";
import { axios, getCookieStorage, timezone } from "@/utils";
import { APP_VERSION } from "@/constants";
import { saveAllCategoryAnswers } from "@/utils/saveEngine";

// Types
interface SaveAnswerPayload {
  answer: string;
  nutrition_customer_onboarding: number;
  question: number;
  answer_unit: string | null;
  selected_option: number | null;
}

interface SaveCategoryAnswersParams {
  questions: any[];
  nutritionCustomerOnboardingId: number;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const PAGINATION_LIMIT_FOR_FETCHING_QUESTIONS = 100;


const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const getNutritionOnboardingData = createAsyncThunk(
  "getNutritionOnboardingData",
  async (packId?: number, { rejectWithValue }) => {
    const fetchWithRetry = async (retryCount = 0): Promise<any> => {
      try {
        const accessToken = getCookieStorage("__authAccessToken");
        
        if (!accessToken) {
          throw new Error("No access token found");
        }

        const url = packId 
          ? `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/diet-plan/my-diet-plan?customer_nutrition_onboarding=${packId}`
          : `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/diet-plan/my-diet-plan`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Customertimezone: timezone,
            Version: APP_VERSION,
          },
        });

        return response;
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          await delay(RETRY_DELAY * (retryCount + 1));
          return fetchWithRetry(retryCount + 1);
        }
        throw error;
      }
    };

    try {
      return await fetchWithRetry();
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const getQuestionModules = createAsyncThunk(
  "getQuestionModules",
  async (packId?: number, { rejectWithValue }) => {
    const fetchWithRetry = async (retryCount = 0): Promise<any> => {
      try {
        const accessToken = getCookieStorage("__authAccessToken");
        
        if (!accessToken) {
          throw new Error("No access token found");
        }

        const url = packId
          ? `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/question-module/?nutrition_onboarding=${packId}`
          : `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/question-module`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Customertimezone: timezone,
          },
        });

        return response;
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          await delay(RETRY_DELAY * (retryCount + 1));
          return fetchWithRetry(retryCount + 1);
        }
        throw error;
      }
    };

    try {
      return await fetchWithRetry();
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

interface GetQuestionsParams {
  categoryId: number;
  packId?: number;
}

export const getQuestionsForCategory = createAsyncThunk(
  "getQuestionsForCategory",
  async (params: GetQuestionsParams, { rejectWithValue }) => {
    const { categoryId, packId } = params;
    const fetchWithRetry = async (retryCount = 0): Promise<any> => {
      try {
        const accessToken = getCookieStorage("__authAccessToken");
        
        if (!accessToken) {
          throw new Error("No access token found");
        }

        const url = packId
          ? `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/question/?limit=${PAGINATION_LIMIT_FOR_FETCHING_QUESTIONS}&question_module=${categoryId}&nutrition_onboarding_id=${packId}`
          : `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/question/?limit=${PAGINATION_LIMIT_FOR_FETCHING_QUESTIONS}&question_module=${categoryId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Customertimezone: timezone,
          },
        });

        return { ...response, categoryId };
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          await delay(RETRY_DELAY * (retryCount + 1));
          return fetchWithRetry(retryCount + 1);
        }
        throw error;
      }
    };

    try {
      return await fetchWithRetry();
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const saveCategoryAnswers = createAsyncThunk(
  "saveCategoryAnswers",
  async (params: SaveCategoryAnswersParams, { dispatch, rejectWithValue }) => {
    const { questions, nutritionCustomerOnboardingId } = params;
    
    try {
      const result = await saveAllCategoryAnswers(
        questions,
        nutritionCustomerOnboardingId,
        (progress) => {
          // Dispatch progress updates to Redux
          dispatch({ type: 'nutritionOnboarding/setSavingProgress', payload: progress });
        }
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save answers');
      }
      
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save answers');
    }
  }
);

export const submitOnboardingForm = createAsyncThunk(
  "submitOnboardingForm",
  async (nutritionOnboardingId: number, { rejectWithValue }) => {
    try {
      const accessToken = getCookieStorage('__authAccessToken');
      
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await axios.patch(
        `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/nutritioncustomeronboarding/${nutritionOnboardingId}/`,
        {
          is_onboarding_form_filled: true
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);